import {Component, OnDestroy, OnInit} from '@angular/core';
import {Location} from '@angular/common';
import {Subject} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {FormBuilder} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {AdminPanelState} from '../../store/reducers/admin-panel.reducer';
import {getSelectedCompany} from '../../store/selectors/admin-panel.selector';
import {CompanyModel} from '../../models/company.model';
import {AdminPanelConfig} from '../../admin-panel.config';
import {AddUserCommand, UpdateUserCommand} from '../../commands/admin-panel.command';
import {
  AddUserAction, GetCompanyDetailAction,
  UpdateUserAction
} from '../../store/actions/admin-panel.action';
import {UpdateUserComponent} from '../update-user/update-user.component';
import {AddUserComponent} from '../add-user/add-user.component';
import {take, takeUntil} from 'rxjs/operators';
import {SmartGridService} from '@core/services/smart-grid.service';
import {MatDialog} from '@angular/material/dialog';
import {PlatformCommand} from '@shared/commands/command-base';
import {Api} from '@shared/constants/common-field.constants';
import {CommandService} from '@core/services/command.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import { FuseSplashScreenService } from '@barebone-fuse/@fuse/services/splash-screen.service';

@Component({
    standalone: false,
    selector: 'lib-detail-company',
  templateUrl: './detail-company.component.html',
  styleUrls: ['./detail-company.component.scss']
})
export class DetailCompanyComponent implements OnInit, OnDestroy {

  item: any;
  isEditing: any;
  company: any;
  sourceType = 'Company-User';
  unsubscribe$ = new Subject<void>();
  detailForm: any;
  companyId: string;
  companySource: string;

  constructor(public dialog: MatDialog,
              private activatedRoute: ActivatedRoute,
              private formBuilder: FormBuilder,
              private store: Store<AdminPanelState>,
              private commandService: CommandService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private snackBarService: SnackBarService,
              private smartGridService: SmartGridService,
              private location: Location) {
    this.company = null;
    this.companyId = '';
    this.detailForm = null;
    this.companySource = '';
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((p: any) => {
      this.companyId = p['companyId'];
      this.store.dispatch(new GetCompanyDetailAction(this.companyId));

      AdminPanelConfig.setCompanyId(this.sourceType, this.companyId);
      this.smartGridService.initDataProvider(this.sourceType);
    });

    this.store.pipe(select(getSelectedCompany), takeUntil(this.unsubscribe$)).subscribe(c => {
      this.company = c;

      if (this.company) {
        let domainValue = '';
        let descriptionValue = '';
        let companyNameValue = '';
        if (this.company.domain ) {
          domainValue = this.company.domain;
        }
        if (this.company.description) {
          descriptionValue = this.company.description;
        }
        if (this.company.companyName) {
          companyNameValue = this.company.companyName;
        }

        this.companySource = this.company.source.toLowerCase();

        this.detailForm = this.formBuilder.group({
          companyId: [this.company.companyId],
          companyName: [companyNameValue],
          description: [descriptionValue],
          source: [this.company.source],
          domain: [domainValue],
          isActive: [this.company.isActive]
        });

        this.detailForm.disable();
        if (this.companySource.includes('orbitax')) {
          this.detailForm.controls['companyName'].enable();
          this.detailForm.controls['description'].enable();
          this.detailForm.controls['domain'].enable();
          this.detailForm.controls['isActive'].enable();
        }

        this.isEditing = {
          companyName: false,
          description: false,
          domain: false
        };
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  back() {
    /*this.router.navigate(AdminPanelConfig.CompanyGridUrl(), {relativeTo: this.activatedRoute.parent});*/
    this.location.back();
  }

  addUser() {
    const dialogRef = this.dialog.open(AddUserComponent, {
      width: '600px',
      disableClose: true,
      data: {company: this.company}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.save) {
        const user = result.user;
        const command = new AddUserCommand(user);
        this.store.dispatch(new AddUserAction(command));
      }
    });
  }

  executeCompanyConfigDialogCommand() {
    const companyConfigDialogCommand = new PlatformCommand('CompanyConfigDialogCommand', Api.AdminPanel);
    companyConfigDialogCommand.setValue('CompanyId', this.companyId);
    this.commandService.executePlatformCommand(companyConfigDialogCommand).subscribe(res => {
      this.snackBarService.showInformation('Configuration Saved');
      console.log(res);
    }, err => {
      this.snackBarService.showError(err.fieldValues['SnackBarMessage']);
      console.log(err);
    });
  }

  onSelectionChanged(event) {

  }

  // update user through modal
  onValueChanged(event) {
    if (event.action && event.action.id && event.action.id === 'Edit') {
      this.updateUser(event.item);
    }
  }

  updateUser(item) {
    const dialogRef = this.dialog.open(UpdateUserComponent, {
      width: '600px',
      disableClose: true,
      data: {user: item.cells}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.save) {
        const user = result.user;
        const command = new UpdateUserCommand(user);
        this.store.dispatch(new UpdateUserAction(command));
      }
    });
  }

  editField(field) {
    this.resetEditing();
    this.isEditing[field] = true;
  }

  saveFieldData(event, field) {
    this.detailForm.controls[field].setValue(event.text);
    this.resetEditing();
    this.updateCompanyData();
  }

  resetEditing() {
    for (const field in this.isEditing) {
      if (this.isEditing.hasOwnProperty(field)) {
        this.isEditing[field] = false;
      }
    }
  }

  updateCompanyData() {
    this.detailForm.controls['companyName'].setValue(this.detailForm.controls['companyName'].value.trim());
    this.detailForm.controls['description'].setValue(this.detailForm.controls['description'].value.trim());
    this.detailForm.controls['domain'].setValue(this.detailForm.controls['domain'].value.trim());
    this.detailForm.controls['isActive'].setValue(this.detailForm.controls['isActive'].value);

    const company = new CompanyModel({
      companyId: this.company.companyId,
      companyName: this.detailForm.controls['companyName'].value,
      description: this.detailForm.controls['description'].value,
      registrationDate: this.company.registrationDate,
      registeredBy: this.company.registeredBy,
      source: this.company.source,
      domain: this.detailForm.controls['domain'].value,
      isActive: this.detailForm.controls['isActive'].value
    });

    const command = new PlatformCommand('UpdateCompanyPlatformCommand', Api.AdminPanel);
    command.setValue("Company", company);
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(res => {
        this.fuseSplashScreenService.hide();
        this.snackBarService.showToastNotification(res.fieldValues['SnackBarMessage'])
      }, err => {
        this.fuseSplashScreenService.hide();
        console.error(err);
        this.snackBarService.showError('Can not update company at the moment');
    });
  }
}
